import PropTypes from 'prop-types';
import { FunnelSimple as ListFilter, X } from '@phosphor-icons/react';
import { Chip } from '../../components/ui/Chip';
import { Button } from '../../components/ui/button';
import { useTranslation } from '../../i18n/I18nContext';

/**
 * Шапка фильтров каталога витрины (#1335, доска «B2 · Two rows»).
 *
 * Две строки над сеткой вместо пяти: строка поиска (марка · модель · цена ·
 * год · More) и строка быстрых чипов (кузов, топливо, спецпредложения) с
 * переключателем «Available only» и числом машин справа. Сортировки нет —
 * порядок «Recommended».
 *
 * Только витрина: у дилера каталог прежний (ветка в `CarsPage`), этот
 * компонент там не рендерится. Поля приходят готовыми элементами из
 * `CarsPage` — состояние фильтров живёт в адресе страницы, и держать его
 * второй раз здесь незачем.
 *
 * На телефоне строка поиска не помещается: вместо неё кнопка «Filters», и
 * все поля открываются тем же разделом «More» — стопкой.
 *
 * @param {object} props
 * @param {React.ReactNode[]} props.fields — поля строки поиска (марка, модель, цена, год).
 * @param {React.ReactNode[]} props.moreFields — поля раздела «More».
 * @param {boolean} props.moreOpen
 * @param {() => void} props.onToggleMore
 * @param {number} props.moreCount — сколько фильтров из «More» выбрано.
 * @param {number} props.activeCount — все выбранные фильтры (бейдж на телефоне).
 * @param {Array<{key: string, label: string, count: number, selected: boolean, onClick: () => void}>} props.chips
 * @param {React.ReactNode} props.availableToggle
 * @param {string} props.countText
 * @param {boolean} props.hasFilters
 * @param {() => void} props.onClear
 * @param {boolean} props.isMobile
 * @param {boolean} [props.compact] — панель прилипла: только применённые
 *   фильтры тегами и кнопка, раскрывающая все поля.
 * @param {Array<{key: string, label: string, onRemove: () => void}>} [props.applied]
 * @param {() => void} [props.onOpenAll]
 */
export const ShowcaseFilterBar = ({
  fields,
  moreFields,
  moreOpen,
  onToggleMore,
  moreCount,
  activeCount,
  chips,
  availableToggle,
  countText,
  hasFilters,
  onClear,
  isMobile,
  compact = false,
  applied = [],
  onOpenAll,
}) => {
  const { t } = useTranslation();

  const chipRow = (
    <div
      className="catalog-bar__chips"
      role="group"
      aria-label={t('showcase.catalog.filters', 'Filters')}
    >
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          selected={chip.selected}
          aria-pressed={chip.selected}
          onClick={chip.onClick}
          className="catalog-bar__chip"
        >
          {chip.label}
          <span className="catalog-bar__chip-count">{chip.count}</span>
        </Chip>
      ))}
    </div>
  );

  const clear = hasFilters && (
    <button type="button" className="catalog-bar__clear" onClick={onClear}>
      {t('showcase.catalog.clear', 'Clear filters')}
    </button>
  );

  // Прилипшая панель (#1335): только применённые фильтры, по тегу на фильтр,
  // каждый снимается нажатием. Все поля — по «More» (на телефоне «Filters»).
  // Без применённых фильтров прилипшая панель на десктопе — только верхняя
  // строка карточки поиска, без тегов и чипов (ниже, `searchOnly`).
  const searchOnly = compact && !isMobile && applied.length === 0;

  if (compact && !searchOnly) {
    const removeHint = t('showcase.catalog.removeFilter', 'remove filter');
    const tags = (
      <div className="catalog-bar__chips catalog-bar__tags">
        {applied.map((filter) => (
          <button
            key={filter.key}
            type="button"
            className="catalog-bar__tag"
            onClick={filter.onRemove}
            aria-label={`${filter.label}, ${removeHint}`}
          >
            <span className="catalog-bar__tag-label">{filter.label}</span>
            <X size={14} weight="bold" aria-hidden="true" />
          </button>
        ))}
      </div>
    );
    if (isMobile) {
      return (
        <div className="catalog-bar catalog-bar--compact catalog-bar--mobile">
          <div className="catalog-bar__row catalog-bar__row--compact">
            <Button
              variant="secondary"
              onClick={onOpenAll}
              aria-expanded={false}
              className="catalog-bar__toggle"
            >
              <ListFilter size={18} aria-hidden="true" />
              {t('showcase.catalog.filters', 'Filters')}
              {activeCount > 0 && (
                <span className="cars-page__badge">{activeCount}</span>
              )}
            </Button>
            {applied.length > 0 ? (
              tags
            ) : (
              <p className="catalog-bar__count">{countText}</p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="catalog-bar catalog-bar--compact">
        <div className="catalog-bar__row">
          {tags}
          <div className="catalog-bar__end">
            {clear}
            <p className="catalog-bar__count">{countText}</p>
            <Button
              variant="ghost"
              onClick={onOpenAll}
              aria-expanded={false}
              className="catalog-bar__more"
            >
              <ListFilter size={18} aria-hidden="true" />
              {t('showcase.catalog.more', 'More')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="catalog-bar catalog-bar--mobile">
        <div className="catalog-bar__row">
          <Button
            variant="secondary"
            onClick={onToggleMore}
            aria-expanded={moreOpen}
            className="catalog-bar__toggle"
          >
            <ListFilter size={18} aria-hidden="true" />
            {t('showcase.catalog.filters', 'Filters')}
            {activeCount > 0 && (
              <span className="cars-page__badge">{activeCount}</span>
            )}
          </Button>
          {availableToggle}
        </div>
        {moreOpen && (
          <div className="catalog-bar__stack">{[...fields, ...moreFields]}</div>
        )}
        {chipRow}
        <div className="catalog-bar__row">
          <p className="catalog-bar__count">{countText}</p>
          {clear}
        </div>
      </div>
    );
  }

  return (
    <div className="catalog-bar">
      {/* Одна карточка: строка поиска, а «More» раскрывает её вниз второй
          строкой — пробег, кузов, топливо — в той же плоскости. */}
      <div
        className={`catalog-bar__panel${
          moreOpen ? ' catalog-bar__panel--open' : ''
        }`}
      >
        <div className="catalog-bar__search">
          {fields.map((field, i) => (
            <div key={i} className="catalog-bar__field">
              {field}
            </div>
          ))}
          <Button
            variant="ghost"
            onClick={onToggleMore}
            aria-expanded={moreOpen}
            className="catalog-bar__more"
          >
            <ListFilter size={18} aria-hidden="true" />
            {/* «More» / «Less», как в прежних фильтрах: кнопка говорит, что
                сделает. Число выбранных — только на закрытой: в открытой
                панели выбранное и так видно. */}
            {moreOpen
              ? t('showcase.catalog.less', 'Less')
              : t('showcase.catalog.more', 'More')}
            {!moreOpen && moreCount > 0 && (
              <span className="cars-page__badge">{moreCount}</span>
            )}
          </Button>
        </div>
        {moreOpen && (
          <div className="catalog-bar__search catalog-bar__extra">
            {moreFields.map((field, i) => (
              <div key={i} className="catalog-bar__field">
                {field}
              </div>
            ))}
          </div>
        )}
      </div>
      {!searchOnly && (
        <div className="catalog-bar__row">
          {chipRow}
          <div className="catalog-bar__end">
            {clear}
            {availableToggle}
            <p className="catalog-bar__count">{countText}</p>
          </div>
        </div>
      )}
    </div>
  );
};

ShowcaseFilterBar.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.node).isRequired,
  moreFields: PropTypes.arrayOf(PropTypes.node).isRequired,
  moreOpen: PropTypes.bool.isRequired,
  onToggleMore: PropTypes.func.isRequired,
  moreCount: PropTypes.number.isRequired,
  activeCount: PropTypes.number.isRequired,
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      selected: PropTypes.bool.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ).isRequired,
  availableToggle: PropTypes.node.isRequired,
  countText: PropTypes.string.isRequired,
  hasFilters: PropTypes.bool.isRequired,
  onClear: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  compact: PropTypes.bool,
  applied: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onRemove: PropTypes.func.isRequired,
    })
  ),
  onOpenAll: PropTypes.func,
};

export default ShowcaseFilterBar;
